import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Plus, Trash2, Link, Eye, Copy, Check, FileText,
  FileLock, FileCheck, ChevronRight, X, ExternalLink,
  Calendar, User, Building
} from 'lucide-react';
import { documentsApi } from '../services/api';
import toast from 'react-hot-toast';

const TEMPLATES = [
  {
    type: 'nda',
    label: 'NDA',
    title: 'Geheimhaltungsvereinbarung (NDA)',
    description: 'Vertraulichkeitsvereinbarung zwischen Ihnen und dem Kunden.',
    icon: FileLock,
    variables: ['projektname', 'ort', 'kunde_firma', 'kunde_adresse'],
  },
  {
    type: 'av_vertrag',
    label: 'AV-Vertrag',
    title: 'Auftragsverarbeitungsvertrag',
    description: 'DSGVO-konformer Vertrag zur Auftragsverarbeitung.',
    icon: FileText,
    variables: ['projektname', 'ort', 'kunde_firma', 'kunde_adresse'],
  },
  {
    type: 'finale_abnahme',
    label: 'Finale Abnahme',
    title: 'Finale Abnahme',
    description: 'Abnahmeprotokoll nach Projektabschluss.',
    icon: FileCheck,
    variables: ['projektname', 'ort', 'kunde_firma', 'kunde_adresse', 'projektbeschreibung', 'offene_punkte'],
  },
];

const STATUS_CONFIG = {
  draft: {
    label: 'Entwurf',
    className: 'bg-slate-100 text-slate-600 border-slate-200',
    icon: null,
  },
  sent: {
    label: 'Versendet',
    className: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: null,
  },
  viewed: {
    label: 'Geöffnet',
    className: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: Eye,
  },
  signed: {
    label: 'Unterschrieben ✓',
    className: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    icon: null,
  },
};

function StatusBadge({ status, signedAt }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const Icon = config.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${config.className}`}
    >
      {Icon && <Icon className="w-3 h-3" />}
      {config.label}
      {status === 'signed' && signedAt && (
        <span className="ml-1 font-normal opacity-75">
          · {new Date(signedAt).toLocaleDateString('de-DE')}
        </span>
      )}
    </span>
  );
}

function TypeBadge({ type }) {
  const tpl = TEMPLATES.find((t) => t.type === type);
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
      {tpl ? tpl.label : type}
    </span>
  );
}

function EmptyState({ onNew }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
        <FileText className="w-8 h-8 text-slate-400" />
      </div>
      <h3 className="text-slate-800 font-semibold text-lg mb-1">
        Noch keine Dokumente
      </h3>
      <p className="text-slate-500 text-sm mb-6 max-w-xs">
        Erstellen Sie NDA, AV-Verträge oder Abnahmeprotokolle und senden Sie diese direkt zur digitalen Unterzeichnung.
      </p>
      <Button
        onClick={onNew}
        className="bg-amber-500 hover:bg-amber-600 text-white gap-2"
      >
        <Plus className="w-4 h-4" />
        Neues Dokument erstellen
      </Button>
    </div>
  );
}

export default function DocumentManager({ activeCompany, user }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formVariables, setFormVariables] = useState({
    projektname: '',
    ort: '',
    kunde_firma: '',
    kunde_adresse: '',
    projektbeschreibung: '',
    offene_punkte: '',
  });
  const [creating, setCreating] = useState(false);

  // Preview dialog
  const [previewDoc, setPreviewDoc] = useState(null);

  // Link dialog
  const [linkDialog, setLinkDialog] = useState({ open: false, docId: null, url: '' });
  const [generatingLink, setGeneratingLink] = useState(false);
  const [copied, setCopied] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (activeCompany?.id) {
      loadDocuments();
    } else {
      setDocuments([]);
      setLoading(false);
    }
  }, [activeCompany?.id]);

  async function loadDocuments() {
    try {
      setLoading(true);
      const res = await documentsApi.getAll(activeCompany.id);
      setDocuments(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Fehler beim Laden der Dokumente');
    } finally {
      setLoading(false);
    }
  }

  function openCreateDialog() {
    setStep(1);
    setSelectedTemplate(null);
    setFormVariables({
      projektname: '',
      ort: '',
      kunde_firma: '',
      kunde_adresse: '',
      projektbeschreibung: '',
      offene_punkte: '',
    });
    setCreateOpen(true);
  }

  function handleTemplateSelect(tpl) {
    setSelectedTemplate(tpl);
    setStep(2);
  }

  function handleVariableChange(key, value) {
    setFormVariables((prev) => ({ ...prev, [key]: value }));
  }

  async function handleCreate() {
    if (!selectedTemplate) return;
    if (!formVariables.projektname.trim()) {
      toast.error('Bitte Projektname eingeben');
      return;
    }
    if (!formVariables.ort.trim()) {
      toast.error('Bitte Ort eingeben');
      return;
    }
    if (!formVariables.kunde_firma.trim()) {
      toast.error('Bitte Kundenfirma eingeben');
      return;
    }

    try {
      setCreating(true);
      const payload = {
        company_id: activeCompany.id,
        type: selectedTemplate.type,
        variables: {
          projektname: formVariables.projektname,
          ort: formVariables.ort,
          kunde_firma: formVariables.kunde_firma,
          kunde_adresse: formVariables.kunde_adresse,
          ...(selectedTemplate.type === 'finale_abnahme' && {
            projektbeschreibung: formVariables.projektbeschreibung,
            offene_punkte: formVariables.offene_punkte,
          }),
        },
      };
      await documentsApi.create(payload);
      toast.success('Dokument erstellt');
      setCreateOpen(false);
      loadDocuments();
    } catch (err) {
      console.error(err);
      toast.error('Fehler beim Erstellen des Dokuments');
    } finally {
      setCreating(false);
    }
  }

  async function handleGenerateLink(doc) {
    try {
      setGeneratingLink(true);
      const res = await documentsApi.generateLink(doc.id);
      const viewHash = res.data?.view_hash || res.data?.viewHash;
      const url = `${window.location.origin}/documents/sign/${viewHash}`;
      setLinkDialog({ open: true, docId: doc.id, url });
      loadDocuments();
    } catch (err) {
      console.error(err);
      toast.error('Fehler beim Generieren des Links');
    } finally {
      setGeneratingLink(false);
    }
  }

  async function handleCopyLink() {
    try {
      await navigator.clipboard.writeText(linkDialog.url);
      setCopied(true);
      toast.success('Link kopiert');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Kopieren fehlgeschlagen');
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await documentsApi.delete(deleteTarget.id);
      toast.success('Dokument gelöscht');
      setDeleteTarget(null);
      loadDocuments();
    } catch (err) {
      console.error(err);
      toast.error('Fehler beim Löschen');
    } finally {
      setDeleting(false);
    }
  }

  const tpl = selectedTemplate
    ? TEMPLATES.find((t) => t.type === selectedTemplate.type)
    : null;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Dokumente</h2>
          <p className="text-slate-500 text-sm mt-0.5">
            Rechtliche Dokumente erstellen und zur Unterzeichnung versenden
          </p>
        </div>
        <Button
          onClick={openCreateDialog}
          className="bg-amber-500 hover:bg-amber-600 text-white gap-2"
        >
          <Plus className="w-4 h-4" />
          Neues Dokument
        </Button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : documents.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <EmptyState onNew={openCreateDialog} />
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-5 py-3">
                      Dokument
                    </th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">
                      Typ
                    </th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">
                      Kunde
                    </th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">
                      Status
                    </th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">
                      Erstellt
                    </th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {documents.map((doc) => {
                    const docTpl = TEMPLATES.find((t) => t.type === doc.type);
                    return (
                      <tr key={doc.id} className="hover:bg-slate-50/60 transition-colors group">
                        <td className="px-5 py-3.5">
                          <button
                            onClick={() => setPreviewDoc(doc)}
                            className="font-medium text-slate-800 hover:text-amber-600 transition-colors text-left flex items-center gap-2"
                          >
                            {docTpl && (
                              <docTpl.icon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            )}
                            {doc.title || doc.variables?.projektname || '—'}
                            <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-amber-500" />
                          </button>
                        </td>
                        <td className="px-4 py-3.5">
                          <TypeBadge type={doc.type} />
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="text-sm text-slate-700">
                            {doc.variables?.kunde_firma || '—'}
                          </div>
                          {doc.variables?.kunde_adresse && (
                            <div className="text-xs text-slate-400 mt-0.5">
                              {doc.variables.kunde_adresse}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3.5">
                          <StatusBadge status={doc.status} signedAt={doc.signed_at} />
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="text-sm text-slate-500">
                            {doc.created_at
                              ? new Date(doc.created_at).toLocaleDateString('de-DE')
                              : '—'}
                          </div>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-1.5 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs border-slate-200 text-slate-600 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 gap-1.5"
                              onClick={() => handleGenerateLink(doc)}
                              disabled={generatingLink}
                            >
                              <Link className="w-3.5 h-3.5" />
                              Link generieren
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-slate-400 hover:text-red-600 hover:bg-red-50 px-2"
                              onClick={() => setDeleteTarget(doc)}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Create Dialog ── */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-slate-900">
              {step === 1 ? 'Vorlage wählen' : 'Dokument konfigurieren'}
            </DialogTitle>
          </DialogHeader>

          {step === 1 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 py-2">
              {TEMPLATES.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.type}
                    onClick={() => handleTemplateSelect(t)}
                    className="group flex flex-col items-start p-4 rounded-xl border-2 border-slate-200 hover:border-amber-400 hover:bg-amber-50/40 transition-all text-left focus:outline-none focus:ring-2 focus:ring-amber-400"
                  >
                    <div className="w-10 h-10 rounded-lg bg-slate-100 group-hover:bg-amber-100 flex items-center justify-center mb-3 transition-colors">
                      <Icon className="w-5 h-5 text-slate-500 group-hover:text-amber-600 transition-colors" />
                    </div>
                    <div className="font-semibold text-slate-800 text-sm mb-1">
                      {t.label}
                    </div>
                    <div className="text-xs text-slate-500 leading-snug">
                      {t.description}
                    </div>
                    <div className="mt-3 ml-auto self-end opacity-0 group-hover:opacity-100 transition-opacity">
                      <ChevronRight className="w-4 h-4 text-amber-500" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {step === 2 && tpl && (
            <div className="space-y-4 py-1">
              {/* Back link */}
              <button
                onClick={() => setStep(1)}
                className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 transition-colors"
              >
                ← Vorlage ändern
              </button>

              <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 border border-slate-200">
                <tpl.icon className="w-5 h-5 text-slate-500 flex-shrink-0" />
                <span className="font-medium text-slate-700 text-sm">
                  {tpl.title}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Projektname <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="z.B. Website Relaunch 2026"
                    value={formVariables.projektname}
                    onChange={(e) => handleVariableChange('projektname', e.target.value)}
                    className="border-slate-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Ort <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="z.B. Hamburg"
                    value={formVariables.ort}
                    onChange={(e) => handleVariableChange('ort', e.target.value)}
                    className="border-slate-200"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                  <Building className="w-4 h-4 text-slate-400" />
                  Kundendaten
                </h4>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Firma <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="Musterfirma GmbH"
                    value={formVariables.kunde_firma}
                    onChange={(e) => handleVariableChange('kunde_firma', e.target.value)}
                    className="border-slate-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Adresse
                  </label>
                  <Input
                    placeholder="Musterstraße 1, 20000 Hamburg"
                    value={formVariables.kunde_adresse}
                    onChange={(e) => handleVariableChange('kunde_adresse', e.target.value)}
                    className="border-slate-200"
                  />
                </div>
              </div>

              {tpl.type === 'finale_abnahme' && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-slate-700">
                    Projektdetails
                  </h4>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      Projektbeschreibung
                    </label>
                    <Textarea
                      placeholder="Kurze Beschreibung des abgeschlossenen Projekts…"
                      value={formVariables.projektbeschreibung}
                      onChange={(e) =>
                        handleVariableChange('projektbeschreibung', e.target.value)
                      }
                      className="border-slate-200 resize-none"
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      Offene Punkte
                    </label>
                    <Textarea
                      placeholder="Keine offenen Punkte / Liste offener Punkte…"
                      value={formVariables.offene_punkte}
                      onChange={(e) =>
                        handleVariableChange('offene_punkte', e.target.value)
                      }
                      className="border-slate-200 resize-none"
                      rows={3}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setCreateOpen(false)}
              className="border-slate-200 text-slate-600"
            >
              Abbrechen
            </Button>
            {step === 2 && (
              <Button
                onClick={handleCreate}
                disabled={creating}
                className="bg-amber-500 hover:bg-amber-600 text-white gap-2"
              >
                {creating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Erstelle…
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Dokument erstellen
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Link Dialog ── */}
      <Dialog open={linkDialog.open} onOpenChange={(v) => setLinkDialog((p) => ({ ...p, open: v }))}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Signier-Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <p className="text-sm text-slate-600">
              Diesen Link an den Kunden senden. Dieser kann das Dokument im Browser öffnen und digital unterzeichnen.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-xs text-slate-700 font-mono break-all select-all">
                {linkDialog.url}
              </code>
              <Button
                size="sm"
                variant="outline"
                onClick={handleCopyLink}
                className="flex-shrink-0 border-slate-200 gap-1.5"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 text-emerald-600" />
                    Kopiert
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Kopieren
                  </>
                )}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setLinkDialog({ open: false, docId: null, url: '' })}
              className="border-slate-200 text-slate-600"
            >
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Preview Dialog ── */}
      <Dialog open={!!previewDoc} onOpenChange={(v) => !v && setPreviewDoc(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              {previewDoc && (() => {
                const pt = TEMPLATES.find((t) => t.type === previewDoc.type);
                return pt ? <pt.icon className="w-5 h-5 text-slate-500" /> : null;
              })()}
              {previewDoc?.title || previewDoc?.variables?.projektname || 'Dokument'}
            </DialogTitle>
          </DialogHeader>
          {previewDoc && (
            <div className="space-y-3 py-1">
              <div className="flex items-center gap-3 flex-wrap">
                <TypeBadge type={previewDoc.type} />
                <StatusBadge status={previewDoc.status} signedAt={previewDoc.signed_at} />
                {previewDoc.created_at && (
                  <span className="text-xs text-slate-400 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(previewDoc.created_at).toLocaleDateString('de-DE')}
                  </span>
                )}
              </div>
              {previewDoc.content ? (
                <div
                  className="prose prose-sm prose-slate max-w-none bg-slate-50 rounded-lg border border-slate-200 p-5 text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: previewDoc.content }}
                />
              ) : (
                <div className="bg-slate-50 rounded-lg border border-slate-200 p-5 space-y-2">
                  {previewDoc.variables &&
                    Object.entries(previewDoc.variables).map(([k, v]) => (
                      <div key={k} className="flex gap-2 text-sm">
                        <span className="font-medium text-slate-600 min-w-[160px]">
                          {k.replace(/_/g, ' ')}:
                        </span>
                        <span className="text-slate-700">{v || '—'}</span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPreviewDoc(null)}
              className="border-slate-200 text-slate-600"
            >
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirm Dialog ── */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Dokument löschen?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 py-1">
            Sind Sie sicher? Das Dokument{' '}
            <span className="font-semibold text-slate-800">
              {deleteTarget?.variables?.projektname || deleteTarget?.title || ''}
            </span>{' '}
            wird unwiderruflich gelöscht.
          </p>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              className="border-slate-200 text-slate-600"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600 text-white gap-2"
            >
              {deleting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Lösche…
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  Löschen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
